import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../Components/Loader/Loader";
import { fetchOffices } from "../../Redux/Actions/Offices";
import { fetchContacts, setContactFilters } from "../../Redux/Actions/Contacts";
import "./Contact.scss";

const phoneEntries = (records) =>
  (Array.isArray(records) ? records : []).reduce((entries, record) => {
    if (record && typeof record === "object") {
      Object.entries(record).forEach(([type, number]) => {
        if (number) entries.push({ type, number: String(number) });
      });
    }
    return entries;
  }, []);

const formatPhone = (value = "") => {
  const original = String(value).trim();
  const digits = original.replace(/\D/g, "");
  const local = digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;
  if (local.length !== 10) return original;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
};

const ContactList = ({ contacts }) => {
  if (!contacts.length) {
    return <div className="ds-contacts-empty">No contacts match your filters.</div>;
  }

  return (
    <div className="ds-contact-results">
      {contacts.map((contact) => {
        const name = [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "Unnamed contact";
        const phones = phoneEntries(contact.phone_and_type);
        return (
          <div className="ds-contact-card" key={contact.id}>
            <Link className="ds-contact-card__main" to={`/contact/${contact.id}`}>
              <span className="ds-contact-card__identity">
                <span className="ds-contact-card__name">{name}</span>
                {contact.affiliation && (
                  <span className="ds-contact-card__affiliation">- {contact.affiliation}</span>
                )}
              </span>
              <span className="ds-contact-card__chevron" aria-hidden="true">›</span>
            </Link>
            {(contact.email || phones.length > 0) && (
              <div className="ds-contact-card__details">
                {contact.email && <a href={`mailto:${contact.email}`}>{contact.email}</a>}
                {phones.slice(0, 2).map(({ type, number }, index) => (
                  <a key={`${type}-${index}`} href={`tel:${number.replace(/[^\d+]/g, "")}`}>
                    <span>{type}: </span>{formatPhone(number)}
                  </a>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const Contact = () => {
  const dispatch = useDispatch();
  const filters = useSelector((state) => state.filters || {});
  // Keep the typed fields separate from the applied search.
  const [draftName, setDraftName] = useState(filters.name || "");
  const [draftOffice, setDraftOffice] = useState(String(filters.selectedOffice || ""));
  const [appliedSearch, setAppliedSearch] = useState({
    name: filters.name || "",
    selectedOffice: String(filters.selectedOffice || ""),
  });
  const [offices, setOffices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [searchVersion, setSearchVersion] = useState(0);

  const { isLoading: officesLoading } = useQuery("offices", fetchOffices, {
    onSuccess: (response) => setOffices(Array.isArray(response?.data) ? response.data : []),
    refetchOnWindowFocus: false,
  });
  const { isLoading: contactsLoading, isError, refetch } = useQuery(
    "fetchContacts",
    fetchContacts,
    {
      onSuccess: (response) => setContacts(Array.isArray(response?.data) ? response.data : []),
      refetchOnWindowFocus: false,
    }
  );

  const filteredContacts = useMemo(() => {
    const term = appliedSearch.name.trim().toLocaleLowerCase();
    const office = appliedSearch.selectedOffice;

    return contacts.filter((contact) => {
      const officeMatches = !office || String(contact.office_id) === String(office);
      const fullName = [contact.first_name, contact.middle_name, contact.last_name]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase();
      return officeMatches && (!term || fullName.includes(term));
    });
  }, [contacts, appliedSearch]);

  const applySearch = (event) => {
    event.preventDefault();
    const next = { name: draftName.trim(), selectedOffice: draftOffice };
    setAppliedSearch(next);
    setSearchVersion((version) => version + 1);
    dispatch(setContactFilters({ ...filters, ...next }));
  };

  const clearFilters = () => {
    const cleared = { name: "", selectedOffice: "" };
    setDraftName("");
    setDraftOffice("");
    setAppliedSearch(cleared);
    setSearchVersion((version) => version + 1);
    dispatch(setContactFilters({ ...filters, ...cleared }));
  };
  const loading = officesLoading || contactsLoading;

  return (
    <main className="ds-contacts-page">
      <h1>Contacts</h1>
      <section className="ds-contacts-search" aria-label="Find a contact">
        <div className="ds-contacts-search__heading">
          <h2>Find a Contact</h2>
          <span>Search by name or office</span>
        </div>
        <form onSubmit={applySearch}>
        <div className="ds-contacts-search__fields">
          <label className="ds-contacts-search__name">
            <span>Name</span>
            <input
              type="search"
              placeholder="Enter first or last name"
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
            />
          </label>
          <label className="ds-contacts-search__office">
            <span>Office</span>
            <select value={draftOffice} onChange={(event) => setDraftOffice(event.target.value)}>
              <option value="">All offices</option>
              {offices.map((office) => <option key={office.id} value={office.id}>{office.name}</option>)}
            </select>
          </label>
        </div>
        <div className="ds-contacts-search__actions">
          <button type="submit" className="ds-contacts-search__submit">Search Contacts</button>
          <button type="button" className="ds-contacts-search__clear" onClick={clearFilters}>Clear</button>
        </div>
        </form>
      </section>
      <div className="ds-contacts-results-heading">
        <h2>Contact Results</h2>
        <span>{loading ? "Loading..." : `${filteredContacts.length} contacts`}</span>
      </div>
      {loading ? <Loader /> : isError ? (
        <div className="ds-contacts-empty">
          Unable to load contacts. <button type="button" onClick={() => refetch()}>Retry</button>
        </div>
      ) : <ContactList key={searchVersion} contacts={filteredContacts} />}
    </main>
  );
};

export default Contact;
